import type { ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { CtaLink } from "@/components/marketing/CtaLink";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { SiteLayout } from "./SiteLayout";
import { SiteBreadcrumbs } from "./SiteBreadcrumbs";
import { useT } from "@/lib/i18n/dict";
import { useLocale, localizedPath } from "@/lib/i18n/locale";

export type FeatureBullet = { icon: LucideIcon; title: string; desc: string };
export type DeepDive = {
  eyebrow: string;
  title: string;
  italic: string;
  description: string;
  points: string[];
  image: string;
  imageAlt?: string;
  reverse?: boolean;
};
export type HowStep = { title: string; desc: string };
export type FaqItem = { q: string; a: string };

export type FeaturePageProps = {
  eyebrow: string;
  title: string;
  italic: string;
  description: string;
  primaryCta?: { label: string; to: string };
  secondaryCta?: { label: string; to: string };
  heroImage: string;
  heroImageAlt?: string;
  metrics?: { v: string; l: string }[];
  bullets: FeatureBullet[];
  deepDives?: DeepDive[];
  howItWorks?: HowStep[];
  capabilities: string[];
  faq?: FaqItem[];
  outro: { title: string; italic: string; description: string };
  afterFaq?: ReactNode;
  topBanner?: ReactNode;
};

export function FeaturePage(p: FeaturePageProps) {
  const deepDives = p.deepDives ?? [];
  const t = useT();
  const locale = useLocale();
  const pricingHref = localizedPath("/pricing", locale);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const pageUrl = `https://banana.cards${pathname}`;
  const heroAbs = p.heroImage.startsWith("http")
    ? p.heroImage
    : `https://banana.cards${p.heroImage.startsWith("/") ? "" : "/"}${p.heroImage}`;

  const jsonLdBlocks: Record<string, unknown>[] = [];

  // Service / SoftwareApplication feature schema
  jsonLdBlocks.push({
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${pageUrl}#service`,
    name: p.eyebrow,
    serviceType: p.eyebrow,
    url: pageUrl,
    description: p.description,
    image: heroAbs,
    provider: {
      "@type": "Organization",
      name: "banana.cards",
      url: "https://banana.cards",
      logo: "https://banana.cards/favicon.ico",
    },
    areaServed: "Worldwide",
    hasOfferCatalog: p.capabilities.length
      ? {
          "@type": "OfferCatalog",
          name: `${p.eyebrow} capabilities`,
          itemListElement: p.capabilities.map((c, i) => ({
            "@type": "Offer",
            position: i + 1,
            itemOffered: { "@type": "Service", name: c },
          })),
        }
      : undefined,
  });

  // FAQPage
  if (p.faq && p.faq.length > 0) {
    jsonLdBlocks.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${pageUrl}#faq`,
      mainEntity: p.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  // HowTo
  if (p.howItWorks && p.howItWorks.length > 0) {
    jsonLdBlocks.push({
      "@context": "https://schema.org",
      "@type": "HowTo",
      "@id": `${pageUrl}#howto`,
      name: `How to get started with ${p.eyebrow}`,
      description: p.description,
      image: heroAbs,
      step: p.howItWorks.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.title,
        text: s.desc,
        url: `${pageUrl}#step-${i + 1}`,
      })),
    });
  }

  // WebPage wrapper linking everything
  jsonLdBlocks.push({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${pageUrl}#webpage`,
    url: pageUrl,
    name: p.title,
    description: p.description,
    primaryImageOfPage: heroAbs,
    about: { "@id": `${pageUrl}#service` },
    isPartOf: {
      "@type": "WebSite",
      name: "banana.cards",
      url: "https://banana.cards",
    },
  });

  // Track section alternation. Hero=background, bullets=muted, then alternate.
  // After bullets (muted), deepDive[0] must be background.
  return (
    <SiteLayout>
      {jsonLdBlocks.map((block, i) => (
        <script
          key={i}
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(block) }}
        />
      ))}
      {p.topBanner}
      {/* HERO */}
      <section className="relative overflow-hidden bg-background">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-12 pt-20 md:grid-cols-2 md:px-6">
          <div className="flex flex-col justify-center order-2 md:order-1">
            <SiteBreadcrumbs className="mb-6" currentName={p.eyebrow} />
            <h1 className="font-display text-3xl leading-[1.1] md:text-5xl">
              {p.title} <em className="italic">{p.italic}</em>
            </h1>
            <p className="mt-5 max-w-md text-base text-muted-foreground md:text-lg">{p.description}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaLink
                to={p.primaryCta?.to ?? "/signup"}
                className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:scale-[1.02]"
              >
                {p.primaryCta?.label ?? t("cta.getStartedFree")}
                <ArrowRight className="h-4 w-4" />
              </CtaLink>
              <Link
                to={p.secondaryCta?.to ?? "/pricing"}
                className="inline-flex items-center gap-2 rounded-xl bg-muted px-6 py-3 text-sm font-bold transition hover:bg-muted/70"
              >
                {p.secondaryCta?.label ?? t("cta.seePricing")}
              </Link>
            </div>

            {p.metrics && p.metrics.length > 0 && (
              <dl className="mt-10 grid max-w-md grid-cols-3 gap-6 border-t border-foreground/10 pt-6">
                {p.metrics.map((m) => (
                  <div key={m.l}>
                    <dt className="font-display text-3xl leading-none">{m.v}</dt>
                    <dd className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {m.l}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
          <div className="relative order-1 md:order-2">
            <img
              src={p.heroImage}
              alt={p.heroImageAlt ?? p.title}
              width={1600}
              height={1024}
              className="relative w-full rounded-2xl object-cover"
            />
          </div>
        </div>
      </section>

      {/* 3 BULLETS */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="grid gap-4 md:grid-cols-3">
          {p.bullets.map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.title} className="rounded-2xl bg-card p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-lg font-bold">{b.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{b.desc}</p>
              </div>
            );
          })}
          </div>
        </div>
      </section>

      {/* DEEP DIVES */}
      {deepDives.map((d, i) => (
        <section key={d.title + i} className={i % 2 === 0 ? "bg-background" : "bg-muted"}>
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 md:grid-cols-2 md:px-6">
            <div className={`relative ${d.reverse ? "md:order-2" : ""}`}>
              <img
                src={d.image}
                alt={d.imageAlt ?? d.title}
                loading="lazy"
                width={1600}
                height={1024}
                className="relative w-full rounded-2xl object-cover"
              />
            </div>
            <div className={d.reverse ? "md:order-1" : ""}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{d.eyebrow}</p>
              <h2 className="mt-3 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
                {d.title} <em className="italic">{d.italic}</em>
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">{d.description}</p>
              <ul className="mt-6 grid gap-2.5">
                {d.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-2.5 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
                    <span className="font-medium">{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      ))}

      {/* HOW IT WORKS */}
      {p.howItWorks && p.howItWorks.length > 0 && (
      <section className={deepDives.length % 2 === 0 ? "bg-muted" : "bg-background"}>
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t("feat.howItWorks")}</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            {t("feat.liveInMinutes.title")} <em className="italic">{t("feat.liveInMinutes.italic")}</em>
          </h2>
          </div>
          <ol className="mt-12 grid gap-4 md:grid-cols-4">
          {p.howItWorks.map((s, i) => (
            <li
              key={s.title}
              className={`relative rounded-2xl p-6 ${
                deepDives.length % 2 === 0 ? "bg-card" : "bg-muted"
              }`}
            >
              <span className="absolute -top-3 left-6 inline-flex h-7 items-center justify-center rounded-full bg-foreground px-3 text-[11px] font-bold text-background">
                {t("feat.step")} {i + 1}
              </span>
              <h3 className="mt-3 text-base font-bold">{s.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            </li>
          ))}
          </ol>
        </div>
      </section>
      )}

      {/* CAPABILITIES */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t("feat.capabilities")}</p>
              <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
                {t("feat.builtTo.title")} <em className="italic">{t("feat.builtTo.italic")}</em>
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
                {t("feat.capabilities.desc")}
              </p>
              <Link
                to={pricingHref}
                className="mt-6 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 hover:underline"
              >
                {t("feat.comparePlans")} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <ul className="grid gap-3 self-center sm:grid-cols-2">
              {p.capabilities.map((c) => (
                <li key={c} className="flex items-start gap-2 rounded-xl bg-muted p-4 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
                  <span className="font-semibold">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {p.faq && p.faq.length > 0 && (
      <section className="bg-muted">
        <div className="mx-auto max-w-4xl px-4 py-24 md:px-6">
          <div className="mb-10 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t("feat.faq")}</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            {t("feat.questions.title")} <em className="italic">{t("feat.questions.italic")}</em>
          </h2>
          </div>
          <div className="divide-y divide-foreground/10 rounded-2xl bg-card">
          {p.faq.map((f) => (
            <details key={f.q} className="group p-5">
              <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm font-bold">
                {f.q}
                <ArrowRight className="h-4 w-4 shrink-0 transition group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
          </div>
        </div>
      </section>
      )}

      {p.afterFaq}

      {/* OUTRO CTA */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-24 pt-4 md:px-6">
          <div className="relative overflow-hidden rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.15),transparent_50%)]" aria-hidden />
          <Sparkles className="mx-auto h-6 w-6 text-brand-lime" />
          <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
            {p.outro.title} <em className="italic text-brand-lime">{p.outro.italic}</em>
          </h2>
          <p className="mx-auto mt-5 max-w-lg text-base text-background/70">{p.outro.description}</p>
          <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
            <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
              {t("cta.getStartedFree")}
            </CtaLink>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
              {t("cta.talkToSales")}
            </Link>
          </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
